import { useCallback, useReducer, useRef } from "react"
import { useTranslation } from "react-i18next"

import { pickChangedFields } from "../lead-workspace-patch.ts"
import {
  type WorkspaceCardStatus,
  workspaceCardEditReducer,
  workspaceCardInitialState
} from "./workspace-card-edit-state.ts"
import { useRegisterWorkspaceCard } from "./workspace-edit-guard.tsx"

interface UseWorkspaceCardEditOptions<V extends object> {
  cardId: string
  values: V
  onSave: (changed: Partial<V>) => Promise<boolean>
}

interface WorkspaceCardEdit<V extends object> {
  status: WorkspaceCardStatus
  editing: boolean
  saving: boolean
  dirty: boolean
  error: string | null
  values: V
  startEdit: () => void
  setValues: (patch: Partial<V>) => void
  cancel: () => void
  save: () => Promise<boolean>
}

function useWorkspaceCardEdit<V extends object>({
  cardId,
  values,
  onSave
}: UseWorkspaceCardEditOptions<V>): WorkspaceCardEdit<V> {
  const { t } = useTranslation()
  const [state, dispatch] = useReducer(
    workspaceCardEditReducer<V>,
    undefined,
    workspaceCardInitialState<V>
  )

  const pristine = useRef(values)
  pristine.current = values

  const changed = state.draft ? pickChangedFields(values, state.draft) : {}
  const dirty = Object.keys(changed).length > 0

  const changedRef = useRef(changed)
  changedRef.current = changed
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty

  const startEdit = useCallback(
    () => dispatch({ type: "edit", values: pristine.current }),
    []
  )
  const setValues = useCallback(
    (patch: Partial<V>) => dispatch({ type: "change", values: patch }),
    []
  )
  const cancel = useCallback(() => dispatch({ type: "cancel" }), [])
  const discard = useCallback(() => dispatch({ type: "discard" }), [])

  const save = useCallback(async () => {
    if (!dirtyRef.current) {
      dispatch({ type: "cancel" })
      return true
    }

    dispatch({ type: "save" })

    try {
      if (await onSave(changedRef.current)) {
        dispatch({ type: "saved" })
        return true
      }
      dispatch({ type: "failed", error: t("leadWorkspace.card.saveFailed") })
    } catch (error) {
      dispatch({
        type: "failed",
        error:
          (error instanceof Error && error.message) ||
          t("leadWorkspace.card.saveFailed")
      })
    }

    return false
  }, [onSave, t])

  useRegisterWorkspaceCard(cardId, dirty, discard)

  return {
    status: state.status,
    editing: state.status !== "view",
    saving: state.status === "saving",
    dirty,
    error: state.error,
    values: state.draft ?? values,
    startEdit,
    setValues,
    cancel,
    save
  }
}

export { type WorkspaceCardEdit, useWorkspaceCardEdit }
